import { getApiKey, getCognitoToken, getProjectId } from "../lib/auth";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) || "https://code-insights-dev-api.agentic-ace.io";

// ci_ API key and Cognito ID token are parallel auth paths — prefer whichever
// this browser session actually signed in with; the API key wins if both
// happen to be present since it's project-scoped and more specific.
function getAuthToken(): string | null {
  return getApiKey() || getCognitoToken();
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  const key = getAuthToken();
  if (key) headers["Authorization"] = `Bearer ${key}`;
  const r = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}: ${await r.text()}`);
  if (r.status === 204) return undefined as unknown as T;
  return (await r.json()) as T;
}

async function uploadFile<T>(path: string, form: FormData): Promise<T> {
  const key = getAuthToken();
  const headers: Record<string, string> = {};
  if (key) headers["Authorization"] = `Bearer ${key}`;
  // Do NOT set content-type — the browser sets it with the boundary for multipart
  const r = await fetch(`${BASE}${path}`, { method: "POST", headers, body: form });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}: ${await r.text()}`);
  return (await r.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
  upload: <T>(path: string, form: FormData) => uploadFile<T>(path, form),
};

export function projectScope(suffix = "", projectIdOverride?: string): string {
  const pid = projectIdOverride ?? getProjectId();
  if (!pid) throw new Error("not authenticated");
  return `/api/v1/projects/${pid}${suffix}`;
}

export type Project = { id: string; name: string; description: string | null; created_at: string };

// Org-scoped: resolves the organization from the caller's JWT/API key
// server-side, no project id needed — used by the Cognito-only login flow.
export const orgApi = {
  listMyProjects: () => api.get<Project[]>("/api/v1/organizations/org/projects"),
  listMyRepos: () => api.get<OrgRepo[]>("/api/v1/repos"),
};

export type Repo = {
  id: string; repo_url: string; ref: string;
  last_commit_sha: string | null; display_name: string | null;
  source_type: string; created_at: string;
};
export type OrgRepo = Repo & { project_id: string; scoped_id: string };
export type IndexJob = {
  id: string; state: string; commit_sha: string | null;
  started_at: string | null; finished_at: string | null;
  error: string | null; stats: Record<string, unknown> | null;
};
export type SymbolHit = {
  id: string; fq_name: string; kind: string; file_path: string;
  start_line: number | null; end_line: number | null; language: string | null;
};
export type CallEdge = { from_id: string; to_id: string; kind: string; file_path: string | null; line: number | null };
export type ModuleDependencyEdge = { source: string; target: string; weight: number };
export type ImpactedSymbol = { id: string; fq_name: string; kind: string; file_path: string; depth: number };
export type ImpactedFile = { path: string; symbol_count: number };
export type Amplifier = { id: string; fan_in: number; reason: string };
export type RiskScore = { score: number; band: "LOW" | "MED" | "HIGH"; factors: Record<string, number> };
export type BlastResponse = {
  target: Record<string, unknown>; depth: number;
  impacted_symbols: ImpactedSymbol[]; impacted_files: ImpactedFile[];
  impacted_tests: ImpactedSymbol[]; amplifiers: Amplifier[]; risk: RiskScore;
};
